<template>
  <DesignCollapseTemplate collapseId="customize">
    <template slot="header">Customize</template>
    <template slot="content">
      <b-form class="mt-4">
        <b-form-group label-class="punch-card-form-label" label="Card Color">
          <b-row>
            <b-col class="col-sm-6 col-md-4">
              <div class="d-flex align-items-start">
                <div class="mr-2 flex-grow-1">
                  <b-form-input type="color" id="punch-card-campaign-card-color"
                    v-model="campaignData.design.customize.campaign_card_color"></b-form-input>
                  <small class="text-muted">{{
                    campaignData.design.customize.campaign_card_color
                  }}</small>
                </div>
                <img src="@/assets/images/icons/colorpicker.svg" alt="Color Picker Icon" class="mt-3" />
              </div>
            </b-col>
          </b-row>
          <b-form-invalid-feedback
            :force-show="$v.campaign_card_color.$errors.length > 0 || !!apiErrors.campaign_card_color">
            <template v-if="!!apiErrors.campaign_card_color">{{ apiErrors.campaign_card_color }}</template>
            <p v-else class="mb-0" v-for="error in $v.campaign_card_color.$errors" :key="error.$uid">{{ error.$message
            }}</p>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-class="punch-card-form-label" label="Brand Logo">
          <ImageUpload ref="brandLogoImageUploader" class="mb-4 pb-1" :extensions="['.jpg', '.jpeg', '.png']"
            recommendation="Accepts .jpg, .jpeg, .png." :enableCropping="true" @onPreviewImage="setBrandLogo"
            @onClearImage="clearImage('image')" @hasWarning="setBrandLogoWarningMessage" :showWarningMessage="false"
            :isThumbNail="true" :imgRaw="campaignData.design.customize.image"
            :imgUploadId="`punchCardBrandLogo-${source}`" :key="`punchCardBrandLogo-${source}`" />
          <b-form-invalid-feedback
            :force-show="$v.image.$errors.length > 0 || !!brandLogoUploadWarning || !!apiErrors.image">
            <template v-if="!!brandLogoUploadWarning || !!apiErrors.image">{{ brandLogoUploadWarning || apiErrors.image
            }}</template>
            <template v-else>
              <p class="mb-0" v-for="error in $v.image.$errors" :key="error.$uid">{{ error.$message }}</p>
            </template>
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-class="punch-card-form-label" label="Background Image">
          <ImageUpload ref="backgroundImageUploader" class="mb-4 pb-1" :extensions="['.jpg', '.jpeg', '.png']"
            recommendation="Accepts .jpg, .jpeg, .png." :enableCropping="true" @onPreviewImage="setBackgroundImage"
            @onClearImage="clearImage('campaign_background_image')" @hasWarning="setBackgroundImageWarningMessage"
            :showWarningMessage="false" :isThumbNail="true"
            :imgRaw="campaignData.design.customize.campaign_background_image"
            :imgUploadId="`punchCardBackgroundImage-${source}`" :key="`punchCardBackgroundImage-${source}`" />
          <b-form-invalid-feedback
            :force-show="!!backgroundImageWarningMessage || !!apiErrors.campaign_background_image">
            {{ backgroundImageWarningMessage || apiErrors.campaign_background_image }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group label-class="punch-card-form-label" label="Select a Layout">
          <b-button id="button-punch-card-campaign-layout-1"
            :class="['p-1 shadow-none mr-3', { 'selected-card-layout': campaignData.design.customize.campaign_layout == 1 }]"
            @click="campaignData.design.customize.campaign_layout = 1">
            <img src="@/assets/images/icons/punch-card/card-layout-1.png" alt="Card Layout 1" class="img-fluid">
          </b-button>
          <b-button id="button-punch-card-campaign-layout-2"
            :class="['p-1 shadow-none', { 'selected-card-layout': campaignData.design.customize.campaign_layout == 2 }]"
            @click="campaignData.design.customize.campaign_layout = 2">
            <img src="@/assets/images/icons/punch-card/card-layout-2.png" alt="Card Layout 2" class="img-fluid">
          </b-button>
          <b-form-invalid-feedback :force-show="!!apiErrors.campaign_layout">
            {{ apiErrors.campaign_layout }}
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form>
    </template>
  </DesignCollapseTemplate>
</template>

<script>
import { computed, defineComponent, getCurrentInstance, ref, onMounted, onBeforeUnmount } from '@vue/composition-api';
import DesignCollapseTemplate from './DesignCollapseTemplate.vue';
import useVuelidate from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { fieldRequiredErrorMessage } from '../../punch.card.utilities';
import { mapFields } from "vuex-map-fields";
import ImageUpload from "@/components/common/ImageUpload.vue";

export default defineComponent({
  props: {
    source: {
      type: String,
      default: "create"
    }
  },
  components: { DesignCollapseTemplate, ImageUpload },
  computed: {
    ...mapFields("punchCard", ["campaignData"])
  },
  setup(_, { root }) {
    const currentInstance = getCurrentInstance();

    const form = computed(() => currentInstance.proxy.campaignData.design.customize || {});

    const rules = {
      campaign_card_color: {
        required: helpers.withMessage(fieldRequiredErrorMessage, required)
      },
      image: {
        required: helpers.withMessage(fieldRequiredErrorMessage, required)
      }
    };

    const $v = useVuelidate(rules, form);

    const setBrandLogo = (logo) => {
      currentInstance.proxy.campaignData.design.customize.image = logo;
    };

    const clearImage = (field) => {
      currentInstance.proxy.campaignData.design.customize[field] = null;
    };

    const brandLogoUploadWarning = ref(null);

    const setBrandLogoWarningMessage = (message) => {
      brandLogoUploadWarning.value = message;
    };

    const setBackgroundImage = (background) => {
      currentInstance.proxy.campaignData.design.customize.campaign_background_image = background;
    };

    const backgroundImageWarningMessage = ref(null);

    const setBackgroundImageWarningMessage = (message) => {
      backgroundImageWarningMessage.value = message;
    };

    onMounted(() => {
      root.$on("validateForm", () => {
        $v.value.$touch();

        if (
          $v.value.$dirty &&
          $v.value.$invalid &&
          currentInstance.proxy.campaignData.active === "design"
        ) {
          currentInstance.proxy.campaignData.designVisibility.customize = true;
        }
      });
    });

    onBeforeUnmount(() => {
      root.$off("validateForm");
    });

    const apiErrors = computed(() => root.$store.state.punchCard.errors || {});

    return {
      form,
      $v,
      setBrandLogo,
      clearImage,
      setBrandLogoWarningMessage,
      setBackgroundImage,
      setBackgroundImageWarningMessage,
      brandLogoUploadWarning,
      backgroundImageWarningMessage,
      apiErrors
    }
  }
});
</script>

<style lang="scss" scoped>
button[id^="button-punch-card-campaign-layout-"] {
  border-color: #D8DCDE;
  background-color: #F7F7F7 !important;
  width: 78px;
  height: 110px;
  transition: all 0.2s ease;

  &.selected-card-layout {
    border: solid 2px #87929D;
  }
}
</style>
